import { ref, readonly } from 'vue'
import {
  createEducationalOrganizationSchema,
  createCourseSchema,
  createArticleSchema,
  createWebSiteSchema,
  getBaseSeoConfig,
  getRobotsContent,
  sanitizeSeoData,
  generateContentUrl,
  createContentBreadcrumbs,
  buildAlternateUrls,
  getSupportedLocales,
  isValidLocale,
  resolveLocalizedString,
} from './helpers'
import { SeoHeadBuilder } from './helpers/headBuilder'
import type {
  SupportedLocale,
  LocalizedContent,
  ContentType,
  BreadcrumbLabels,
} from './helpers'
import type { SEOData, ResolvedSeoData } from './types'

export type { SEOData } from './types'

const DEFAULT_LOCALE = 'en'

export function useSeo() {
  const i18n = useI18n()
  const { t } = i18n
  const route = useRoute()

  const seoData = ref<Pick<ResolvedSeoData, 'title' | 'description' | 'image' | 'url'>>({
    title: '',
    description: '',
    image: '',
    url: '',
  })

  const getCurrentLocale = (): SupportedLocale => {
    const loc = (i18n.locale?.value as string) || DEFAULT_LOCALE
    return isValidLocale(loc) ? loc : DEFAULT_LOCALE
  }

  const getConfigForLocale = (loc: string) => {
    const messages = i18n.getLocaleMessage(loc)
    const fallback = i18n.getLocaleMessage(DEFAULT_LOCALE)
    return getBaseSeoConfig(loc, messages, fallback)
  }

  const getPageDefaults = (): SEOData => {
    const locale = getCurrentLocale()
    const config = getConfigForLocale(locale)
    return {
      title: config.defaultTitle,
      description: config.defaultDescription,
      image: config.defaultImage,
      url: `${config.baseUrl}${route.fullPath}`,
      locale,
      type: 'website',
      robots: getRobotsContent(),
    }
  }

  /** Розв'язує локалізовані поля та санітизує (SRP: один крок підготовки). */
  const resolveAndSanitize = (data: SEOData, locale: string): ResolvedSeoData => {
    const title = resolveLocalizedString(data.title, locale)
    const description = resolveLocalizedString(data.description, locale)
    const config = getConfigForLocale(locale)
    const sanitized = sanitizeSeoData(
      { title, description, image: data.image, url: data.url },
      config
    )
    return {
      ...sanitized,
      locale,
      type: data.type ?? 'website',
      robots: data.robots ?? getRobotsContent(),
      keywords: data.keywords,
    } satisfies ResolvedSeoData
  }

  /** Декларативна настройка SEO: дані → head. */
  const setupSeo = (customData?: SEOData) => {
    const defaults = getPageDefaults()
    const finalData: SEOData = { ...defaults, ...customData, locale: getCurrentLocale() }
    const locale = finalData.locale ?? getCurrentLocale()

    const resolved = resolveAndSanitize(finalData, locale)
    seoData.value = {
      title: resolved.title,
      description: resolved.description,
      image: resolved.image,
      url: resolved.url,
    }

    const headConfig = SeoHeadBuilder.build(resolved, finalData, {
      getConfigForLocale,
      routePath: route.fullPath,
      getSupportedLocales,
    })
    useHead(headConfig)
  }

  /** SEO для локалізованого контенту: alternate URLs + breadcrumbs → setupSeo. */
  const setupLocalizedSeo = (config: {
    contentType: ContentType
    title: LocalizedContent['title']
    description?: LocalizedContent['description']
    slug: LocalizedContent['slug']
    image?: string
    categorySlug?: string
    categoryTitle?: string
    schema?: Record<string, unknown>
    keywords?: string[]
  }) => {
    const locale = getCurrentLocale()
    const baseUrl = getConfigForLocale(locale).baseUrl ?? 'https://bibleawr.com'

    const alternateUrls = config.slug
      ? buildAlternateUrls(
          config.contentType,
          config.slug,
          baseUrl,
          config.contentType === 'course' ? config.categorySlug : undefined
        )
      : undefined

    const currentTitle = resolveLocalizedString(config.title, locale)
    const labels: BreadcrumbLabels = {
      home: t('seo.breadcrumbs.home'),
      courses: t('seo.breadcrumbs.courses'),
      articles: t('seo.breadcrumbs.articles'),
      categories: t('seo.breadcrumbs.categories'),
      lessons: t('seo.breadcrumbs.lessons'),
      events: t('seo.breadcrumbs.events'),
    }
    const breadcrumbs = createContentBreadcrumbs(
      config.contentType,
      currentTitle,
      locale,
      baseUrl,
      labels,
      config.categorySlug,
      config.categoryTitle
    )

    setupSeo({
      title: config.title,
      description: config.description,
      image: config.image,
      slug: config.slug,
      alternateUrls,
      breadcrumbs,
      schema: config.schema,
      keywords: config.keywords,
    })
  }

  const setupCourseSeo = (courseData: {
    title: LocalizedContent['title']
    description?: LocalizedContent['description']
    slug: LocalizedContent['slug']
    image?: string
    instructor?: string
    categorySlug?: string
    categoryTitle?: string
  }) => {
    const locale = getCurrentLocale()
    const currentTitle = resolveLocalizedString(courseData.title, locale)
    const currentDescription = resolveLocalizedString(courseData.description, locale)

    const schema = createCourseSchema({
      name: currentTitle,
      description: currentDescription,
      provider: courseData.instructor ?? 'Bible School',
      url: courseData.slug
        ? generateContentUrl('course', courseData.slug, locale, undefined, courseData.categorySlug)
        : undefined,
    })

    setupLocalizedSeo({
      contentType: 'course',
      title: courseData.title,
      description: courseData.description,
      slug: courseData.slug,
      image: courseData.image,
      categorySlug: courseData.categorySlug,
      categoryTitle: courseData.categoryTitle,
      schema
    })
  }

  const setupArticleSeo = (articleData: {
    title: LocalizedContent['title']
    description?: LocalizedContent['description']
    slug: LocalizedContent['slug']
    image?: string
    author?: string
    datePublished?: string
    dateModified?: string
    keywords?: string[]
  }) => {
    const locale = getCurrentLocale()
    const currentTitle = resolveLocalizedString(articleData.title, locale)
    const currentDescription = resolveLocalizedString(articleData.description, locale)
    const keywordsString = articleData.keywords?.length
      ? articleData.keywords.join(', ')
      : undefined

    const schema = createArticleSchema({
      headline: currentTitle,
      description: currentDescription,
      author: articleData.author ?? 'Bible School',
      datePublished: articleData.datePublished ?? new Date().toISOString().split('T')[0],
      dateModified: articleData.dateModified ?? new Date().toISOString().split('T')[0],
      url: articleData.slug ? generateContentUrl('article', articleData.slug, locale) : undefined,
      keywords: keywordsString,
    })

    setupLocalizedSeo({
      contentType: 'article',
      title: articleData.title,
      description: articleData.description,
      slug: articleData.slug,
      image: articleData.image,
      schema,
      keywords: articleData.keywords,
    })
  }

  return {
    seoData: readonly(seoData),
    setupSeo,
    setupLocalizedSeo,
    setupCourseSeo,
    setupArticleSeo,
    getPageDefaults,
    getCurrentLocale,
    createEducationalOrganizationSchema,
    createCourseSchema,
    createArticleSchema,
    createWebSiteSchema,
  }
}